// src/components/UserFiles/UserFileGroupCard.jsx
import React, { useState } from 'react';
import { 
  Box, 
  Chip, 
  Typography, 
  IconButton, 
  Tooltip, 
  CircularProgress, 
  Stack
} from '@mui/material';
import DataObjectIcon from '@mui/icons-material/DataObject';

import UserFileRow from './UserFileRow';
import { downloadFileForPreview } from '../../../services/userFileService';
import PreviewJSON from '../../PIIAnonymizerPage/PIIFileUploadPanel/PreviewJSON';

const UserFileGroupCard = ({
  group,
  t,
  onDownload,
  onDelete,
  onOpenSignDialog,
  onOpenSignaturesDialog,
}) => {
  // Pick username from one of the files in the group
  const username = group?.files?.find(Boolean)?.owner_username || null;

  // 1. Separate JSON files from regular files
  const regularFiles = group.files.filter((f) => !f.filename.toLowerCase().endsWith('.json'));
  const jsonFiles = group.files.filter((f) => f.filename.toLowerCase().endsWith('.json'));

  // 2. Extract unique origins from ALL files in the group
  const uniqueOrigins = Array.from(
    new Set(group.files.map((f) => f.origin).filter(Boolean))
  );

  // 3. State for the JSON Preview Modal
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewFileItem, setPreviewFileItem] = useState(null);
  const [previewLoadingId, setPreviewLoadingId] = useState(null);

  const handleOpenJsonPreview = async (file) => {
    try {
      setPreviewLoadingId(file.id);

      const res = await downloadFileForPreview(file);
      if (!res?.success || !res.data?.blob) {
        window.alert(res?.error || t('files.preview_failed', 'Nepodarilo sa načítať súbor na náhľad.'));
        return;
      }

      setPreviewFileItem({
        blob: res.data.blob,
        name: file.filename,
        downloadName: file.filename,
      });
      setPreviewOpen(true);
    } finally {
      setPreviewLoadingId(null);
    }
  };

  const handleClosePreview = () => {
    setPreviewOpen(false);
    setPreviewFileItem(null);
  };

  // Helper to render the origin chip (reused logic from UserFileRow)
  const renderOriginChip = (origin) => {
    let label = origin;
    let color = 'default';

    if (origin === 'external_anonymizer') {
      label = t('files.origin_external', 'Cenzor AI');
      color = 'info';
    }

    return (
      <Chip
        key={origin}
        label={label}
        size="small"
        color={color}
        variant="outlined"
        sx={{ borderRadius: '6px', height: 22, fontSize: '0.7rem', fontWeight: 600 }}
      />
    );
  };

  return (
    <>
      <Box
        sx={{
          mb: 1, // Reduced bottom margin since we use gap in the parent list now
          borderRadius: 3,
          bgcolor: group.color || 'background.default',
          border: '1px solid',
          borderColor: 'divider',
          overflow: 'hidden',
          width: '100%',
          boxShadow: 1, // Using native MUI shadow instead of hardcoded rgba
        }}
      >
        {/* HEADER SECTION */}
        <Box
          sx={{
            px: { xs: 2, sm: 3 },
            py: 2,
            display: 'flex',
            alignItems: { xs: 'flex-start', sm: 'center' },
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'space-between',
            gap: 2,
            borderBottom: '1px solid',
            borderColor: 'divider', // Theme-aware semantic border
          }}
        >
          {/* LEFT SIDE: Group Info */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Chip
                size="small"
                label={t('files.anonymization_group', 'ID procesu')}
                sx={{ fontSize: '0.75rem', fontWeight: 600, bgcolor: 'action.selected' }} // Semantic highlight
              />
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ fontFamily: 'monospace', fontWeight: 600 }}
              >
                {String(group.id)}
              </Typography>
              
              {/* Display Unique Origins Here */}
              {uniqueOrigins.map((origin) => renderOriginChip(origin))}
            </Box>

            {username && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                {t('files.anonymized_by', 'Anonymizované používateľom')}{' '}
                <Typography component="span" variant="caption" sx={{ fontWeight: 600, color: 'text.primary' }}>
                  {username}
                </Typography>
              </Typography>
            )}
          </Box>

          {/* RIGHT SIDE: JSON Preview Buttons */}
          {jsonFiles.length > 0 && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {jsonFiles.map((jsonFile) => (
                <Tooltip key={jsonFile.id} title={t('files.preview_json', `Náhľad ${jsonFile.filename}`)}>
                  <span>
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={() => handleOpenJsonPreview(jsonFile)}
                      disabled={previewLoadingId === jsonFile.id}
                      sx={{
                        bgcolor: 'background.paper',
                        border: '1px solid',
                        borderColor: 'divider',
                        boxShadow: 1,
                        p: 0.75,
                        transition: 'all 0.2s ease',
                        '&:hover': { bgcolor: 'action.hover', borderColor: 'primary.main' },
                      }}
                    >
                      {previewLoadingId === jsonFile.id ? (
                        <CircularProgress size={18} color="inherit" />
                      ) : (
                        <DataObjectIcon fontSize="small" />
                      )}
                    </IconButton>
                  </span>
                </Tooltip>
              ))}
            </Box>
          )}
        </Box>

        {/* FILES LIST (Inner rows) */}
        <Stack 
          spacing={1} 
          sx={{ 
            p: { xs: 1.5, sm: 2 }
          }}
        >
          {regularFiles.map((file) => (
            <UserFileRow
              key={`${group.id}-${file.id}`}
              file={file}
              t={t}
              onDownload={onDownload}
              onDelete={onDelete}
              onOpenSignDialog={onOpenSignDialog}
              onOpenSignaturesDialog={onOpenSignaturesDialog}
              withDivider={false} // Since we use Stack spacing, we don't need inner dividers
            />
          ))}
        </Stack>
      </Box>

      {/* JSON Preview Modal rendered at the group level */}
      <PreviewJSON
        open={previewOpen}
        fileItem={previewFileItem}
        onClose={handleClosePreview}
      />
    </>
  );
};

export default UserFileGroupCard;